import React, { PropsWithChildren, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { JobStatusEnum } from '@api/ringil-api.v2.tsx';
import { PushContext } from '@hooks/usePush/PushContext.tsx';
import { usePushEvent } from '@hooks/usePush/usePushEvent.ts';
import { useAuthReady } from '@hooks/useAuthReady';

/**
 * Client-side job events framework: a normalized store of the user's async jobs, fed by
 * job.progress / job.finished push payloads on top of a single REST seed.
 *
 * Contract per consumer ("1 GET + payloads + 1 GET"):
 *  - a job detail page fetches its type-specific detail ONCE on mount,
 *  - renders live progress purely from the store / item payloads (zero REST during the run),
 *  - refetches ONCE when the job reaches a terminal status (reconciliation, `onFinished`).
 * Counters in events are absolute snapshots and `item` is an item's final state, so lost
 * frames only delay the display — the finish refetch always reconciles.
 *
 * Degraded mode is centralized here: while push is not connected and any job is active,
 * ONE slow listMyAsyncJobs reseed poll keeps snapshots (and thus onFinished detection) alive.
 */

export interface JobSnapshot {
  jobId: string;
  jobType?: string;
  applicationId?: string;
  status: JobStatusEnum;
  processed: number;
  success: number;
  failed: number;
  total?: number;
  updatedAt: number;
}

export interface JobItemEvent {
  itemKey?: string;
  itemStatus: string;
  /** Type-specific payload (same shape the type's detail endpoint parses); opaque to the framework. */
  resultJson?: string;
  errorMessage?: string;
}

type ItemListener = (item: JobItemEvent) => void;

interface JobEventsContextType {
  jobs: Map<string, JobSnapshot>;
  addItemListener: (jobId: string, listener: ItemListener) => () => void;
  trackJob: (jobId: string) => void;
}

export const JobEventsContext = createContext<JobEventsContextType>({
  jobs: new Map(),
  addItemListener: () => () => {},
  trackJob: () => {},
});

const TERMINAL_STATUSES: JobStatusEnum[] = [JobStatusEnum.DONE, JobStatusEnum.ERROR, JobStatusEnum.CANCELLED];
const ACTIVE_STATUSES: JobStatusEnum[] = [JobStatusEnum.PENDING, JobStatusEnum.RUNNING];
const MAX_STORE_SIZE = 100;
const DEGRADED_RESEED_MS = 15000;
const CONNECTED_RESEED_MS = 60000;

export const isTerminalJobStatus = (status: JobStatusEnum | undefined) => status != null && TERMINAL_STATUSES.includes(status);

export const JobEventsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { listMyAsyncJobs } = useContext(ApiContext);
  const { connectionState } = useContext(PushContext);
  const authReady = useAuthReady();
  const [jobs, setJobs] = useState<Map<string, JobSnapshot>>(new Map());
  const itemListenersRef = useRef<Map<string, Set<ItemListener>>>(new Map());
  const reseedDebounceRef = useRef<number | null>(null);
  const listMyAsyncJobsRef = useRef(listMyAsyncJobs);
  listMyAsyncJobsRef.current = listMyAsyncJobs;

  const upsert = (prev: Map<string, JobSnapshot>, snapshot: JobSnapshot): Map<string, JobSnapshot> => {
    const next = new Map(prev);
    next.set(snapshot.jobId, snapshot);
    if (next.size > MAX_STORE_SIZE) {
      // Drop the oldest terminal snapshots first; active jobs are never evicted
      [...next.values()]
        .filter(s => isTerminalJobStatus(s.status))
        .sort((a, b) => a.updatedAt - b.updatedAt)
        .slice(0, next.size - MAX_STORE_SIZE)
        .forEach(s => next.delete(s.jobId));
    }
    return next;
  };

  const reseed = () => {
    listMyAsyncJobsRef.current(50, list => {
      setJobs(prev => {
        let next = prev;
        list.forEach(j => {
          next = upsert(next, {
            jobId: j.jobId,
            jobType: j.jobType,
            applicationId: j.applicationId,
            status: j.jobStatus,
            processed: j.processedCount,
            success: j.successCount,
            failed: j.failedCount,
            total: j.totalCount,
            updatedAt: Date.now(),
          });
        });
        // Evict stale FOREIGN snapshots: colleagues' jobs arrive via company-jobs hints but are
        // never in /jobs/my, so a hint-then-silence job would look "active" forever — pinning
        // hasActiveJob (and this poll) for the whole session. Anything non-terminal, absent from
        // the reseed and quiet for 5+ minutes is dropped; a live job re-appears on its next hint.
        const myIds = new Set(list.map(j => j.jobId));
        const staleCutoff = Date.now() - 5 * 60 * 1000;
        const stale = [...next.values()].filter(s => !isTerminalJobStatus(s.status) && !myIds.has(s.jobId) && s.updatedAt < staleCutoff);
        if (stale.length > 0) {
          const pruned = new Map(next);
          stale.forEach(s => pruned.delete(s.jobId));
          next = pruned;
        }
        return next;
      });
    });
  };

  const reseedDebounced = () => {
    if (reseedDebounceRef.current != null) return;
    reseedDebounceRef.current = window.setTimeout(() => {
      reseedDebounceRef.current = null;
      reseed();
    }, 500);
  };

  // Seed once the user is logged in — see useAuthReady. (Sentry RINGIL-APP-WM.)
  useEffect(() => {
    if (!authReady) return;
    reseed();
  }, [authReady]);

  useEffect(
    () => () => {
      if (reseedDebounceRef.current != null) window.clearTimeout(reseedDebounceRef.current);
    },
    [],
  );

  usePushEvent(
    e => {
      if (e.jobId == null) return;
      const known = jobsRef.current.has(e.jobId);
      setJobs(prev =>
        upsert(prev, {
          jobId: e.jobId,
          jobType: e.jobType ?? prev.get(e.jobId)?.jobType,
          applicationId: e.applicationId ?? prev.get(e.jobId)?.applicationId,
          status: e.status as JobStatusEnum,
          processed: e.processed ?? 0,
          success: e.success ?? 0,
          failed: e.failed ?? 0,
          total: e.total,
          updatedAt: Date.now(),
        }),
      );
      if (!known) reseedDebounced();
      if (e.item != null) {
        itemListenersRef.current.get(e.jobId)?.forEach(listener => {
          try {
            listener(e.item as JobItemEvent);
          } catch (err) {
            console.error('JobEventsProvider: item listener failed', err);
          }
        });
      }
    },
    { types: ['job.progress', 'job.finished'], onReconnect: () => reseed() },
  );

  // Ref mirror so the push handler can check "known" without stale closure state
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;

  const hasActiveJob = useMemo(() => [...jobs.values()].some(s => ACTIVE_STATUSES.includes(s.status)), [jobs]);

  // Safety reseed while anything is running — one poll for the whole app:
  //  - push disconnected: 15s (primary degraded mode)
  //  - push connected: slow 60s insurance against BE-side dropped hints (Centrifugo
  //    slow-but-connected can drop job.finished on the publisher side; the reseed flips the
  //    snapshot terminal so useJobEvents.onFinished still fires within a minute)
  useEffect(() => {
    if (!hasActiveJob) return;
    const intervalMs = connectionState === 'connected' ? CONNECTED_RESEED_MS : DEGRADED_RESEED_MS;
    const intervalId = setInterval(reseed, intervalMs);
    return () => clearInterval(intervalId);
  }, [connectionState, hasActiveJob]);

  const addItemListener = (jobId: string, listener: ItemListener) => {
    let set = itemListenersRef.current.get(jobId);
    if (set == null) {
      set = new Set();
      itemListenersRef.current.set(jobId, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
      if (set.size === 0) itemListenersRef.current.delete(jobId);
    };
  };

  const trackJob = (jobId: string) => {
    if (!jobsRef.current.has(jobId)) reseedDebounced();
  };

  return <JobEventsContext.Provider value={{ jobs, addItemListener, trackJob }}>{children}</JobEventsContext.Provider>;
};
